import { readableOn, PAGE_BG_LIGHT, isHexColor } from '@essential-support/shared/color';
import { contrastText } from './color.js';
import { getFormatLocale } from './locale.js';

// Relative "time ago" for ticket rows + message timestamps. Localized via
// Intl.RelativeTimeFormat in the user's region locale (narrow = compact, e.g. en
// "5m ago" / "now"; auto so -1 day reads "yesterday").
export function timeAgo(input) {
  if (!input) return '';
  const then = new Date(input).getTime();
  const secs = Math.round((Date.now() - then) / 1000);
  const loc = getFormatLocale();
  const rtf = new Intl.RelativeTimeFormat(loc, { numeric: 'auto', style: 'narrow' });
  if (secs < 45) return rtf.format(0, 'second');
  const mins = Math.round(secs / 60);
  if (mins < 60) return rtf.format(-mins, 'minute');
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return rtf.format(-hrs, 'hour');
  const days = Math.round(hrs / 24);
  if (days < 7) return rtf.format(-days, 'day');
  return new Date(input).toLocaleDateString(loc);
}

export function fullTime(input) {
  if (!input) return '';
  return new Date(input).toLocaleString(getFormatLocale(), {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  });
}

// Locale-aware number formatting.
export function formatNumber(n) {
  return new Intl.NumberFormat(getFormatLocale()).format(n);
}

// Human name for a language tag ("de" → "German" / "Deutsch"), in the viewer's
// own locale via Intl.DisplayNames; falls back to the raw tag.
export function languageName(tag) {
  if (!tag) return '';
  try { return new Intl.DisplayNames([getFormatLocale()], { type: 'language' }).of(tag) || tag; }
  catch { return tag; }
}

// Apply tenant branding colors to the document root as CSS variable overrides.
export function applyBranding(branding) {
  if (!branding) return;
  const root = document.documentElement;
  // Set a RAW brand var, not --heading directly: CSS derives --heading from it per
  // theme (light = the brand color; dark = a neutral light), so a light-oriented brand
  // heading color never renders dark-on-dark in dark mode. Theme toggles need no re-run —
  // the cascade re-derives. (--link is fine as-is: it's a band/button BACKGROUND, and
  // the derived --link-fg already lightens it for dark-mode link TEXT.)
  if (branding.headingColor) root.style.setProperty('--brand-heading', branding.headingColor);
  if (branding.linkColor) {
    root.style.setProperty('--link', branding.linkColor);
    // Light-mode link TEXT: the same RAW-var pattern — the brand color darkened until it
    // reads on the page (a yellow header color would otherwise paint unreadable yellow
    // links). Set --brand-link-fg, never --link-fg: an inline --link-fg would outrank the
    // dark block's lightening.
    root.style.setProperty('--brand-link-fg', readableOn(branding.linkColor, PAGE_BG_LIGHT));
  }
  // The header color IS --link. Pick a readable foreground for the brand text +
  // icons that sit on it: the heading black when the color is light, else white.
  const headerColor = branding.linkColor || null;
  root.style.setProperty('--header-fg', headerColor ? contrastText(headerColor) : '#fff');
  // Match the browser-chrome color (mobile address bar, task switcher) to the header.
  // Default mirrors styles.css `--link` + index.html; the per-workspace manifest carries
  // the same color for the installed PWA (see server/src/branding/index.js).
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) meta.setAttribute('content', headerColor || '#233f64');
}

// The workspace's colors from its last /api/me, per origin (= per workspace host), so the
// next boot's FIRST frame — the portal's header band in App's BootSplash, the frame a
// KB → Tickets view transition fades into — already wears the brand, not the default navy
// (docs/KB.md gotcha 24). Only /api/me writes it (App), never the Settings live preview.
const BRAND_CACHE = 'es_brand';
export function rememberBranding(branding) {
  try {
    localStorage.setItem(BRAND_CACHE, JSON.stringify({
      headingColor: branding?.headingColor || null,
      linkColor: branding?.linkColor || null
    }));
  } catch { /* storage blocked — the band just starts in the default color */ }
}
// main.jsx, before the first render; the real branding replaces it when /api/me lands.
export function applyCachedBranding() {
  let b = null;
  try { b = JSON.parse(localStorage.getItem(BRAND_CACHE) || 'null'); } catch { b = null; }
  if (!b || typeof b !== 'object') return;
  const pick = (v) => (isHexColor(v) ? v : null);
  if (pick(b.linkColor) || pick(b.headingColor)) applyBranding({ headingColor: pick(b.headingColor), linkColor: pick(b.linkColor) });
}
